import React from 'react';
import { CircularProgress, Grid2 as Grid, Divider } from '@mui/material';
import { AddOutlined, DeleteOutlined } from '@mui/icons-material';
import { Button, NumberSelect, TextField, TextSelect } from './input';
import { Metadata } from './Metadata';
import { AutomaticPlayerActionType } from '../../enums';
import { ActionRequest } from '../../actions';
import { styled } from '@mui/styles';

interface Props {
    readonly styled: StyledProps;
    readonly actions?: Entities.AutomaticPlayerAction[];
    readonly readOnly?: boolean;
    readonly style?: React.CSSProperties;
    readonly onUpdateActions?: (actions: Entities.AutomaticPlayerAction[]) => void;
}

interface State {
    readonly loading: boolean;
    readonly actions: Entities.AutomaticPlayerAction[];
    readonly scripts: Entities.CloudCode[];
    readonly methods: string[];
    readonly titleData: Entities.TitleData[];
}

export class AutomatePlayerAction extends React.Component<Props, State> {
    state: State = {
        loading: false,
        actions: [],
        scripts: [],
        methods: [],
        titleData: [],
    };

    async componentDidMount() {
        const { actions } = this.props;
        if (actions) {
            this.setState({actions});
        } else{
            this.addAction();
        }
        await this.getMethods();
    }

    render() {
        const { readOnly, style } = this.props;
        const { loading, actions } = this.state;
        const actionsElements: React.JSX.Element[] = [];
        actions.forEach((a, i) => {
            switch (a.action) {
                case AutomaticPlayerActionType.SendPushNotification:
                    actionsElements.push(this.renderSendPushNotificationAction(i, a.params as Entities.SendPushNotificationParams));
                    break;
                case AutomaticPlayerActionType.ExecuteCloudCode:
                    actionsElements.push(this.renderExecuteCloudCodeAction(i, a.params as Entities.ExecuteCloudCodeParams));
                    break;
            }
        });

        return (
            <Grid container={true} justifyContent="center" spacing={2} style={{ width: '100%', ...style }}>
                <Grid size={12}>
                    {loading && (
                        <CircularProgress style={{margin: 20}}/>
                    )}
                    {!loading && (
                        <>
                            <Grid container={true} spacing={1} style={{width: '100%', margin: 0, padding: 10}}>
                                {actions.length === 0 && (
                                    <Grid size={12} style={{border: '1px solid rgb(223, 223, 223)', padding: 10}}>
                                        No actions
                                    </Grid>
                                )}
                                {actionsElements.map((element, index) => (
                                    <>
                                        {index > 0 && (
                                            <Grid size={12}>
                                                <Divider />
                                            </Grid>
                                        )}
                                        <Grid size={12} style={{ padding: 8 }}>
                                            {element}
                                        </Grid>
                                    </>
                                ))}
                            </Grid>
                        </>
                    )}
                </Grid>
                {!readOnly && (
                    <Grid size={12}>
                        <StyledButton text="Add action" icon={AddOutlined} onClick={this.addAction} />
                    </Grid>
                )}
            </Grid>
        );
    }

    private onChangeActions = (actions: Entities.AutomaticPlayerAction[]) => {
        const { onUpdateActions } = this.props;
        if (!onUpdateActions) {
            return;
        }
        this.setState({ actions });
        onUpdateActions(actions);
    };

    private renderActionType = (index: number, value: AutomaticPlayerActionType) => {
        const { actions, scripts, methods, titleData } = this.state;

        const onChangeMethod = (value: number) => {
            const action =  value as AutomaticPlayerActionType;
            actions[index].action = action;
            const scriptSelected = scripts.find(s => s.live);
            const titleDataSelected = titleData.find(t => t.isDefault);
            switch (action) {
                case AutomaticPlayerActionType.SendPushNotification:
                    actions[index].params = {
                        title: '',
                        body: '',
                    };
                    break;
                case AutomaticPlayerActionType.ExecuteCloudCode:
                    actions[index].params = {
                        revision: scriptSelected ? scriptSelected.revision : -1,
                        titleData: titleDataSelected ? titleDataSelected.version : '',
                        method: methods[0],
                        arguments: '{}',
                    };
                    break;
            }
            this.onChangeActions(actions);
        };

        return (
            <NumberSelect
                label="Type"
                labelId={`action_type_${index}`}
                value={value}
                onChange={onChangeMethod}
            >
                <option key={AutomaticPlayerActionType.SendPushNotification} value={AutomaticPlayerActionType.SendPushNotification}>
                    Send Push Notification
                </option>
                <option key={AutomaticPlayerActionType.ExecuteCloudCode} value={AutomaticPlayerActionType.ExecuteCloudCode}>
                    Execute Cloud Code
                </option>
            </NumberSelect>
        );
    };

    private renderSendPushNotificationAction = (index: number, params: Entities.SendPushNotificationParams) => {
        const { readOnly } = this.props;
        const { actions } = this.state;

        const onTitleChange = (value: string) => {
            params.title = value;
            actions[index].params = params;
            this.onChangeActions(actions);
        };

        const onBodyChange = (value: string) => {
            params.body = value;
            actions[index].params = params;
            this.onChangeActions(actions);
        };

        const onRemove = () => this.removeAction(index);

        return (
            <Grid container={true} spacing={0}>
                <Grid size={12}>
                    {this.renderActionType(index, AutomaticPlayerActionType.SendPushNotification)}
                </Grid>
                <Grid size={12}>
                    <TextField
                        label="Title"
                        fullWidth={true}
                        variant="outlined"
                        value={params.title}
                        onChange={onTitleChange}
                    />
                </Grid>
                <Grid size={12}>
                    <TextField
                        label="Body"
                        fullWidth={true}
                        variant="outlined"
                        value={params.body}
                        onChange={onBodyChange}
                    />
                </Grid>
                {!readOnly && (
                    <Grid size={12}>
                        <StyledButton text="Remove action" icon={DeleteOutlined} onClick={onRemove} />
                    </Grid>
                )}
            </Grid>
        );
    };

    private getMethods = async (revision?: number): Promise<void> => {
        this.setState({loading: true});
        const data = await ActionRequest.get(`cloudCode/${revision}/getMethods`);
        if (!data) {
            return;
        }
        this.setState({
            loading: false,
            scripts: data.scripts,
            methods: data.methods,
            titleData: data.titleData,
        });
    }

    private renderExecuteCloudCodeAction = (index: number, params: Entities.ExecuteCloudCodeParams) => {
        const { readOnly } = this.props;
        const { actions, scripts, methods, titleData } = this.state;

        const onChangeRevision = (revision: number) => {
            params.revision = revision;
            actions[index].params = params;
            this.getMethods(params.revision).then(() => this.onChangeActions(actions));
        };

        const onChangeTitleData = (titleData: string) => {
            params.titleData = titleData;
            actions[index].params = params;
            this.onChangeActions(actions);
        };

        const onChangeMethod = (method: string) => {
            params.method = method;
            actions[index].params = params;
            this.onChangeActions(actions);
        };

        const onChangeArguments = (args: string) => {
            params.arguments = args;
            actions[index].params = params;
            this.onChangeActions(actions);
        };

        const onRemove = () => this.removeAction(index);

        return (
            <Grid container={true} spacing={0} style={{borderBottom: '2px solid rgb(223, 223, 223)', margin: 0, width: '100%'}}>
                <Grid size={{ xs:12, sm:4 }}>
                    {this.renderActionType(index, AutomaticPlayerActionType.ExecuteCloudCode)}
                </Grid>
                <Grid size={{ xs:12, sm:8 }}>
                    <Grid container={true} spacing={2}>
                        <Grid size={12}>
                            <NumberSelect
                                label="Revision"
                                labelId={`revision-label-${index}`}
                                value={params.revision}
                                onChange={onChangeRevision}
                            >
                                {scripts.map(value => (
                                    <option key={value.revision} value={value.revision}>
                                        {`Revision ${value.revision} ${value.live ? '(live)' : ''}`}
                                    </option>
                                ))}
                            </NumberSelect>
                        </Grid>
                        <Grid size={12}>
                            <TextSelect
                                label="Title Data Version"
                                labelId={`title-data-label-${index}`}
                                value={params.titleData}
                                onChange={onChangeTitleData}
                            >
                                {titleData.map(value => (
                                    <option key={value.version} value={value.version}>
                                        {`${value.version} ${value.isDefault ? '(live)' : ''}`}
                                    </option>
                                ))}
                            </TextSelect>
                        </Grid>
                        <Grid size={12}>
                            <TextSelect
                                label="Function Name"
                                labelId={`function-name-label-${index}`}
                                value={params.method}
                                onChange={onChangeMethod}
                            >
                                {methods.map(method => (
                                    <option key={method} value={method}>
                                        {method}
                                    </option>
                                ))}
                            </TextSelect>
                        </Grid>
                        <Grid size={12}>
                            <Metadata
                                styled={this.props.styled}
                                label="Arguments"
                                text={params.arguments}
                                readOnly={readOnly}
                                onChange={onChangeArguments}
                            />
                        </Grid>
                        {!readOnly && (
                            <Grid size={12}>
                                <StyledButton text="Remove action" icon={DeleteOutlined} onClick={onRemove} />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    private addAction = () => {
        const { actions } = this.state;
        const action: Entities.AutomaticPlayerAction = {
            action: AutomaticPlayerActionType.SendPushNotification,
            params: {
                title: '',
                body: '',
            }
        };
        actions.push(action);
        this.onChangeActions(actions);
    }

    private removeAction = (index: number) => {
        const { actions } = this.state;
        this.onChangeActions(actions.filter((v, i) => i !== index));
    }
}

const StyledButton = styled(Button)(({
    color: '#0073bb',
    fontSize: 'smaller',
    borderColor: 'rgba(0, 0, 0, 0)',
    '&:disabled': {
        borderColor: 'rgba(0, 0, 0, 0)',
    },
    '&:hover': {
        backgroundColor: 'transparent',
    }
}));
